import { OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { IScope, IPromise, IQService } from "angular";
import { IModalServiceInstance, IModalService } from "angular-ui-bootstrap";
import { IGoalNode, IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { GoalsTreeService } from "@gtmhub/goals/goalsTreeService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IUserItemAllowedActions } from "@gtmhub/sessions/models/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IStateInit } from "@gtmhub/core/routing";
import { AlignmentLoader, IAlignmentLoadedEventArgs, AlignmentContentState } from "../alignmentLoader";
import { DocumentExporterService, IInsightBoardPdfQuery } from "@gtmhub/document-exporter/document-exporter-service";
import { IGridSortingRule, IGridFilter, toRestLayerRequest, hasFilterValue, RestLayerFilterFactory } from "@gtmhub/shared/components/grid-filtering-bar";
import { IRestLayerRequest } from "@gtmhub/core";
import { IAlignmentTreeFilterResult, INestedTreeGoal, INestedTreeOkrNode } from "../models";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { triggerFileDownload } from "@gtmhub/shared/dom";
import { resetPersistedExpandedItems } from "../utils";
import { filtersOverrideForOkrName, filtersOverrideForTagTitle, separateDynamicValues } from "@gtmhub/goals/filter-utils";
import { CloneOkrsModal, GoalsChangedEventArgs, OkrEvents } from "../..";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { MetricService } from "@gtmhub/okrs/metrics";
import { NestedTreeService } from "../service/nested-tree-service";
import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { INgRedux } from "@gtmhub/state-management";

const flattenGoalsTree = (nodes: IGoalNode[], items = []): IGoal[] => {
  nodes.forEach((n) => {
    if (Array.isArray(n.children) && n.children.length > 0) {
      flattenGoalsTree(n.children, items);
    }

    items.push(n.goal);
  });

  return items;
};

interface INestedTreeReduxScope {
  sessionsIdMap: Record<string, IPlanningSession>;
}

const bindStateToScope = (state: ISessionsStoreState) => {
  return {
    sessionsIdMap: state.sessions.map,
  };
};

export interface IAlignmentNestedScope extends IScope, INestedTreeReduxScope {
  goalsTreeRoot: IGoalNode;
  indicators: {
    reloadGoals: boolean;
    deleting: boolean;
    updating: boolean;
  };
  allowedActions: string[];
  showMetrics: boolean;
  reorderGoalChildrenEnabled: boolean;
  confirmDeleteGoal(goal: IGoal): void;
  exportToPdf(): void;

  isNestedViewV2Enabled: boolean;
  okrsTreeRoot: INestedTreeOkrNode;
  planningSession: IPlanningSession;
  areRootGoalsExpandable: boolean;
  isProgressAggregationDisabled: boolean;
  sorted: boolean;
  confirmDeleteMetric(goalId: string, metricId: string);
  openCloneGoalModal(goalId: string);
  openCloneMetricModal(metricId: string);
  persistExpandedOkr(okrId: string, isExpanded: boolean);
}

export class AlignmentNestedCtrl implements IStateInit {
  private confirmDeletingGoal: IModalServiceInstance;
  private confirmDeletingMetric: IModalServiceInstance;
  private currentSessionId: string;
  private order: IGridSortingRule[];
  private goals: IGoal[];
  private filters: IGridFilter[];
  private contentState: AlignmentContentState;
  private okrEvents: OkrEvents;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$uibModal",
    "$q",
    "GoalService",
    "PermissionsService",
    "GoalsTreeService",
    "AlignmentLoader",
    "UIErrorHandlingService",
    "DocumentExporterService",
    "FeatureToggleV2Service",
    "MetricService",
    "CloneOkrsModal",
    "NestedTreeService",
    "PlanningSessionsActions",
    "$ngRedux",
    "OkrMethodologyFacade",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IAlignmentNestedScope,
    private $uibModal: IModalService,
    private $q: IQService,
    private goalService: GoalService,
    private permissionsService: PermissionsService,
    private goalsTreeService: GoalsTreeService,
    private alignmentLoader: AlignmentLoader,
    private uiErrorHandlingService: UIErrorHandlingService,
    private documentExporterService: DocumentExporterService,
    private featureToggleService: FeatureToggleV2Service,
    private metricService: MetricService,
    private cloneOkrsModal: CloneOkrsModal,
    private nestedTreeService: NestedTreeService,
    private planningSessionsActions: PlanningSessionsActions,
    private $ngRedux: INgRedux,
    private okrMethodologyfacade: OkrMethodologyFacade
  ) {
    this.$rootScope.noNavigation = false;
    this.okrEvents = new OkrEvents(this.$scope);

    this.$scope.confirmDeleteGoal = this.confirmDeleteGoal;
    this.$scope.confirmDeleteMetric = this.confirmDeleteMetric;
    this.$scope.openCloneGoalModal = this.openCloneGoalModal;
    this.$scope.openCloneMetricModal = this.openCloneMetricModal;
    this.$scope.persistExpandedOkr = this.persistExpandedOkr;
    this.$scope.exportToPdf = this.exportToPdf;

    this.$scope.indicators = {
      reloadGoals: true,
      deleting: false,
      updating: false,
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey || e.altKey) && e.keyCode === 80) {
        e.preventDefault();
        e.stopPropagation();
        this.exportToPdf();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    this.$scope.$on("$destroy", () => document.removeEventListener("keydown", onKeyDown));

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)($scope);
    $scope.$on("$destroy", unsubscribe);

    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
  }

  public stateInit(): IPromise<unknown> {
    return this.alignmentLoader.promise().then((args) => this.$rootScope.traceAction("nested_tree_init", () => this.onLoaded(args)));
  }

  private applyStyles = (item: HTMLElement): string => {
    this.loadImageSrc(item, "src");
    this.loadImageSrc(item, "lazy-src");

    return item.innerHTML;
  };

  private loadImageSrc = (element: HTMLElement, attrSelector: "src" | "lazy-src"): void => {
    const images = element.querySelectorAll(`img[${attrSelector}]`);
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const imageSrc = image.getAttribute(attrSelector);
      const decodedImageSrc = decodeURIComponent(imageSrc);
      // directly pass the avatar url from the query params of the gravatar redirect req
      if (decodedImageSrc.includes("gravatar")) {
        const auth0Avatar = decodedImageSrc.split("d=")[1];
        image.setAttribute("src", auth0Avatar);
      } else if (attrSelector === "lazy-src") {
        // load the lazy-src avatar
        image.setAttribute("src", imageSrc);
      }
    }
  };

  private exportToPdf = () => {
    const goalsArray = flattenGoalsTree(this.$scope.goalsTreeRoot.children);
    const nestedEl = document.getElementById("goals-nested");
    const nestedHtml = this.applyStyles(nestedEl);

    // Construct a whole html doc with required style file in head and widgets in body
    // Note: Backend needs to know the style file's name to apply styles in backend => sent in the doc's head
    const htmlDoc = `<!doctype html>
    <html lang="en">
      <head>
        <link rel="stylesheet" href="nested.style.css" type="text/css">
      </head>
      <body>
        <ul>${nestedHtml}</ul>
      </body>
    </html>`;

    const query: IInsightBoardPdfQuery = {
      sourceType: "alignmentNestedView", // necessary for tracking right segment data since the pdf exporter will be used in whole webapp
      payload: this.documentExporterService.minifyAndEncodeHtml(htmlDoc),
      properties: {
        totalGoalsCount: goalsArray.length,
      },
    };

    const titleEl = document.querySelector("title");
    const savedHtmlTitle = titleEl.innerText;

    this.documentExporterService.exportPdf(query).then(
      (pdfStringResponse) => {
        titleEl.innerText = savedHtmlTitle;
        const generatedPdf = new Blob([pdfStringResponse], { type: "application/pdf" });
        triggerFileDownload(generatedPdf, "alignment_nested_view" + "_" + new Date().toLocaleDateString() + ".pdf");
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private onLoaded = (args: IAlignmentLoadedEventArgs): void => {
    this.evaluateCtrlVariables(args);

    this.$scope.$on("alignment.unifiedFiltersChanged", (evt, args: IAlignmentLoadedEventArgs) => this.applyUnifiedFiltersChanges(args));

    // local events
    this.$scope.$on("goalsChanged", () => {
      this.loadGoals();
    });

    this.okrEvents.onGoalsChanged((args) => {
      if (this.$scope.isNestedViewV2Enabled) {
        this.handleGoalsChangedEvent(args);
      } else {
        if (args.reason === "goalChildReordered") {
          this.loadGoals();
        }
      }
    });

    // directive events
    this.$scope.$on("goalsTree.confirmDeleteGoal", (evt, goal: IGoal) => this.confirmDeleteGoal(goal));

    // component events
    this.$scope.$on("nestedTree.confirmDeleteGoal", (evt, goal: IGoal) => this.$scope.confirmDeleteGoal(goal));
    this.$scope.$on("nestedTree.confirmDeleteMetric", (evt, goalId: string, metricId: string) => this.$scope.confirmDeleteMetric(goalId, metricId));
    this.$scope.$on("nestedTree.cloneGoal", (evt, goalId: string) => this.$scope.openCloneGoalModal(goalId));
    this.$scope.$on("nestedTree.cloneMetric", (evt, metricId: string) => this.$scope.openCloneMetricModal(metricId));
    this.$scope.$on("nestedTree.okrExpandToggled", (evt, okrId: string, isExpanded: boolean) => this.$scope.persistExpandedOkr(okrId, isExpanded));

    // sidebar events
    this.$scope.$on("nested.viewModeChanged", (evt, showMetrics: boolean) => this.showMetricsChanged(showMetrics));
    this.$scope.$on("alignment.expandAll", () => this.expandAll());
    this.$scope.$on("alignment.collapseAll", () => this.collapseAll());
    this.$scope.$on("alignment.orderChanged", (evt, orderRules: { order: IGridSortingRule[] }) => this.orderChanged(orderRules.order));

    this.checkForEnabledFeatures().then(() => {
      this.checkPermissions();
      this.loadGoals();
    });

    this.$scope.planningSession = this.$scope.sessionsIdMap[this.currentSessionId];
    this.$scope.isProgressAggregationDisabled = this.okrMethodologyfacade.isProgressAggregationDisabled(this.$scope.planningSession?.settings);
  };

  private evaluateCtrlVariables = (args: IAlignmentLoadedEventArgs): void => {
    this.currentSessionId = args.currentSessionId;
    this.order = args.order;
    this.filters = args.filters;
    this.$scope.showMetrics = args.viewModes.showMetrics;
    this.contentState = args.contentState;
  };

  private checkPermissions = (): void => {
    this.permissionsService.getCurrentUserAllowedActionsPerItem(this.currentSessionId, "session").then(
      (actions: IUserItemAllowedActions) => {
        this.$scope.allowedActions = actions.currentUserActions;
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private expandAll = (): void => {
    if (this.$scope.isNestedViewV2Enabled) {
      this.nestedTreeService.expandAllNodes(this.currentSessionId, this.$scope.okrsTreeRoot);
    } else {
      this.$scope.goalsTreeRoot.goal.filterOutChildren = false;
      const goalIdsToExpand = [];
      for (const goal of this.goals) {
        goal.folding = false;
        goal.folded = false;
        goal.filterOutChildren = false;
        goalIdsToExpand.push(goal.id);
      }
      this.goalsTreeService.expandSet(this.currentSessionId, goalIdsToExpand);
    }
  };

  private collapseAll = (): void => {
    if (this.$scope.isNestedViewV2Enabled) {
      this.nestedTreeService.collapseAllNodes(this.currentSessionId, this.$scope.okrsTreeRoot);
    } else {
      const goalIdsToCollapse = [];
      for (const goal of this.goals) {
        goal.folding = true;
        goal.folded = true;
        goal.filterOutChildren = false;
        goalIdsToCollapse.push(goal.id);
      }
      this.goalsTreeService.collapseSet(this.currentSessionId, goalIdsToCollapse);
    }
  };

  private loadGoals = (): void => {
    this.$scope.indicators.reloadGoals = true;

    this.goalService
      .getAllGoalsV2({ filter: { sessionId: this.currentSessionId }, limit: 200 })
      .then((collection) => {
        this.goals = collection.items;
        return this.recalculateGoalsTree();
      })
      .then(
        () => {
          this.$scope.indicators.reloadGoals = false;
        },
        (error) => this.handleIndicatorError(error, "reloadGoals", true)
      );
  };

  private showMetricsChanged = (showMetrics: boolean): void => {
    this.$scope.showMetrics = showMetrics;
    this.recalculateGoalsTree().catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private orderChanged = (order: IGridSortingRule[]): void => {
    this.order = order;
    this.recalculateGoalsTree()
      .then(() => {
        this.$scope.sorted = true;
      })
      .catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private recalculateGoalsTree = () => {
    let goalsRequest: IRestLayerRequest;
    const hasNonSessionFilters = this.filters.filter((x) => x.fieldName !== "sessionId" && hasFilterValue(x)).length > 0;
    if (hasNonSessionFilters) {
      const filtersOverride: Record<string, RestLayerFilterFactory> = {
        ...filtersOverrideForTagTitle(),
        ...filtersOverrideForOkrName(),
      };

      const separatedFilterValues: IGridFilter[] = separateDynamicValues(angular.copy(this.filters));
      goalsRequest = toRestLayerRequest({ filters: separatedFilterValues, filtersOverride });
    }

    let filterPromise: IPromise<IAlignmentTreeFilterResult>;
    if (goalsRequest) {
      goalsRequest.fields = "_id";
      goalsRequest.limit = -1;

      filterPromise = this.goalService.getGoalsV2(goalsRequest).then((matches) => ({ goalIds: matches.items.map((x) => x.id) }));
    } else {
      filterPromise = this.$q.resolve<IAlignmentTreeFilterResult>(undefined);
    }

    return filterPromise.then((filterResult) => {
      const matchedGoalIds = filterResult ? filterResult.goalIds : [];

      if (this.$scope.isNestedViewV2Enabled) {
        this.$scope.okrsTreeRoot = this.nestedTreeService.constructNestedViewOkrTree({
          goals: this.goals as INestedTreeGoal[],
          sort: this.order,
          matchedGoalIds: matchedGoalIds,
          filtersApplied: hasNonSessionFilters,
          showMetrics: this.$scope.showMetrics,
          sessionId: this.currentSessionId,
        });
        this.$scope.areRootGoalsExpandable = this.$scope.okrsTreeRoot.children.some((rootNode) => rootNode.children.length > 0);
      }

      this.$scope.goalsTreeRoot = this.goalsTreeService.constructGoalsTree(this.currentSessionId, this.goals, this.order, matchedGoalIds);
      this.contentState.hasItems = this.$scope.goalsTreeRoot.children.length > 0;
    });
  };

  private confirmDeleteMetric = (goalId: string, metricId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.confirmDeletingMetric = this.$uibModal.open({
      templateUrl: require("../../../goals/views/confirm-delete-goal.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope) {
          $scope.indicators = ctrl.$scope.indicators;

          $scope.confirm = () => {
            ctrl.deleteMetric(goalId, metricId);
          };
        },
      ],
    });
    this.confirmDeletingMetric.result.finally(() => (this.$scope.indicators.deleting = false));
  };

  private confirmDeleteGoal = (goal: IGoal) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.confirmDeletingGoal = this.$uibModal.open({
      templateUrl: require("../../../goals/views/confirm-delete-goal.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope) {
          $scope.indicators = ctrl.$scope.indicators;

          $scope.confirm = () => {
            ctrl.deleteGoal(goal);
          };
        },
      ],
    });
    this.confirmDeletingGoal.result.finally(() => (this.$scope.indicators.deleting = false));
  };

  private deleteMetric = (goalId: string, metricId: string): void => {
    this.$rootScope.traceAction("delete_metric", () => {
      this.$scope.indicators.deleting = true;
      this.metricService
        .deleteMetric(goalId, metricId)
        .then(() => {
          this.$rootScope.$broadcast("goalsChanged");
          this.$scope.indicators.deleting = false;
          this.confirmDeletingMetric.close();
        })
        .catch((error) => this.handleIndicatorError(error, "deleting", true));
    });
  };

  private deleteGoal = (goal: IGoal) => {
    this.$rootScope.traceAction("delete_goal", () => {
      this.$scope.indicators.deleting = true;
      this.goalService.deleteGoal(goal.id).then(
        () => {
          this.$rootScope.$broadcast("goalsChanged");
          this.$scope.indicators.deleting = false;
          this.confirmDeletingGoal.close();
        },
        (error) => this.handleIndicatorError(error, "deleting", true)
      );
    });
  };

  private openCloneMetricModal = (metricId: string) => {
    this.metricService
      .getMetric(metricId)
      .then((metric) => {
        this.cloneOkrsModal.openForMetricClone(metric);
      })
      .catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private openCloneGoalModal = (goalId: string) => {
    this.goalService
      .getGoal(goalId)
      .then((goal) => {
        this.cloneOkrsModal.openForGoalClone(goal);
      })
      .catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private applyUnifiedFiltersChanges = (args: IAlignmentLoadedEventArgs): void => {
    this.evaluateCtrlVariables(args);
    resetPersistedExpandedItems(this.currentSessionId);
    this.recalculateGoalsTree().catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private checkForEnabledFeatures = (): IPromise<unknown> => {
    const promiseMap = {
      isReorderGoalChildrenEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.FeatureReorderGoalChildren),
      isNestedViewV2Enabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NestedViewV2),
    };
    return this.$q.all(promiseMap).then((resolvedPromises) => {
      this.$scope.isNestedViewV2Enabled = resolvedPromises.isNestedViewV2Enabled;
      this.$scope.reorderGoalChildrenEnabled = resolvedPromises.isReorderGoalChildrenEnabled;
    });
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private persistExpandedOkr = (okrId: string, isExpanded: boolean): void => {
    this.nestedTreeService.toggleExpandItem(this.currentSessionId, okrId, isExpanded);
  };

  private handleGoalsChangedEvent = (args: GoalsChangedEventArgs) => {
    switch (args.reason) {
      case "goalCreated": {
        const goalParentId = args.detail.goal.parentId;
        if (goalParentId) {
          this.persistExpandedOkr(goalParentId, true);
        }
        break;
      }
      case "metricCreated": {
        const metricParentId = args.detail.goalId;
        this.persistExpandedOkr(metricParentId, true);
        break;
      }
      case "goalChildReordered":
        this.loadGoals();
        break;
    }
  };
}
